import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtUSD, fmtNum, relTime, gainClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BookText, Tag as TagIcon, Save, X, Upload, FileUp, Sparkles } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip } from "recharts";

function InsightsCard() {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = () =>
    api.get("/futures/import/insights/history").then(({ data: h }) => setHistory(h || [])).catch(() => {});

  useEffect(() => {
    api.get("/futures/import/insights")
      .then(({ data: d }) => setData(d && d.insights ? d : null))
      .catch(() => {});
    loadHistory();
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/futures/import/insights");
      setData(d);
      toast.success(`Analyzed ${d.fills_analyzed} imported fills`);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  const s = data?.stats;
  const ins = data?.insights;
  return (
    <Card className="border border-gray-200 rounded-2xl p-6" data-testid="insights-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="font-display font-semibold text-lg flex items-center gap-2">
            <Sparkles size={16} /> Batch insights
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Claude reviews your whole imported batch — recurring mistakes, best setups, next steps.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={busy}
          data-testid="insights-run-btn"
          className="rounded-full bg-brand text-white hover:bg-brand-deep h-9"
        >
          <Sparkles size={12} className="mr-2" /> {busy ? "Analyzing…" : data ? "Re-analyze" : "Analyze imported fills"}
        </Button>
      </div>
      {data && (
        <div className="mt-4 space-y-4" data-testid="insights-result">
          {s && (
            <p className="text-xs text-gray-500">
              {s.total_fills} fills · {s.wins}W/{s.losses}L ({s.win_rate}%) ·{" "}
              <span className={gainClass(s.total_realized_pnl)}>{fmtUSD(s.total_realized_pnl)}</span> realized
              {s.profit_factor != null && ` · PF ${s.profit_factor}`}
              {" · avg win "}{fmtUSD(s.avg_win)}{" / avg loss "}{fmtUSD(s.avg_loss)}
            </p>
          )}
          {ins?.summary && <p className="text-sm text-gray-800 leading-relaxed">{ins.summary}</p>}
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              ["Recurring mistakes", ins?.recurring_mistakes, "text-loss"],
              ["Best setups", ins?.best_setups, "text-gain"],
              ["Recommendations", ins?.recommendations, "text-brand"],
            ].map(([title, items, cls]) =>
              items && items.length > 0 ? (
                <div key={title} className="rounded-xl bg-gray-50 p-4">
                  <p className={`text-[10px] uppercase font-bold tracking-[0.1em] ${cls}`}>{title}</p>
                  <ul className="mt-2 space-y-1.5">
                    {items.map((it, i) => (
                      <li key={i} className="text-xs text-gray-700 leading-snug">• {it}</li>
                    ))}
                  </ul>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}
      {history.length > 1 && (
        <div className="mt-4" data-testid="insights-trend">
          <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">
            Habit trend — win rate across analyses
          </p>
          <div style={{ height: 90 }} className="mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={[...history].reverse().map((h) => ({
                  t: h.created_at,
                  win: h.stats?.win_rate ?? 0,
                  pnl: h.stats?.total_realized_pnl ?? 0,
                }))}
                margin={{ top: 6, right: 6, left: 6, bottom: 6 }}
              >
                <XAxis dataKey="t" hide />
                <YAxis hide domain={[0, 100]} />
                <ChartTooltip
                  contentStyle={{ border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 11, padding: "6px 10px" }}
                  labelFormatter={(t) => new Date(t).toLocaleString()}
                  formatter={(v, name) => (name === "win" ? [`${v}%`, "Win rate"] : [fmtUSD(v), "Realized"])}
                />
                <Line type="monotone" dataKey="win" stroke="hsl(var(--brand))" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {history.length > 1 && (
        <div className="mt-4">
          <button
            onClick={() => setShowHistory((v) => !v)}
            data-testid="insights-history-toggle"
            className="text-xs font-medium text-brand hover:text-brand-deep"
          >
            {showHistory ? "Hide history" : `Compare past analyses (${history.length})`}
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1" data-testid="insights-history">
              {history.map((h, i) => (
                <button
                  key={h._id}
                  onClick={() => setData(h)}
                  data-testid={`insights-history-row-${i}`}
                  className={`w-full text-left rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 transition-colors ${
                    data?._id === h._id ? "bg-brand/10 text-brand-deep" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span className="font-medium">{new Date(h.created_at).toLocaleString()}</span>
                  <span>{h.fills_analyzed} fills</span>
                  <span>win rate {h.stats?.win_rate}%</span>
                  <span className={gainClass(h.stats?.total_realized_pnl || 0)}>
                    {fmtUSD(h.stats?.total_realized_pnl || 0)}
                  </span>
                  {h.stats?.profit_factor != null && <span>PF {h.stats.profit_factor}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ImportCard({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [adapter, setAdapter] = useState("auto");
  const [report, setReport] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!csvText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/futures/import", { csv: csvText, adapter });
      setReport(data);
      toast.success(`Imported ${data.imported} fill${data.imported === 1 ? "" : "s"}`);
      if (data.imported > 0) onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const problems = (report?.rows || []).filter((r) => r.status !== "imported");
  return (
    <Card className="border border-gray-200 rounded-2xl p-6" data-testid="import-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="font-display font-semibold text-lg flex items-center gap-2">
            <FileUp size={16} /> Import broker fills (CSV)
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-lg">
            Futures fills only (MES, MNQ, ES, NQ — contract symbols like MESU26 auto-normalize).
            Imported fills are journal-only: they never touch your paper cash, margin or leaderboard.
            TradeStation and Tradovate exports are detected automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={adapter}
            onChange={(e) => setAdapter(e.target.value)}
            data-testid="import-adapter-select"
            className="text-xs font-medium rounded-full bg-gray-100 text-gray-700 px-3 py-2 border-0 focus:ring-brand"
          >
            <option value="auto">Auto-detect</option>
            <option value="generic">Generic CSV</option>
            <option value="tradestation">TradeStation</option>
            <option value="tradovate">Tradovate</option>
          </select>
          <label className="cursor-pointer text-xs font-medium px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="import-file-input" />
            {fileName || "Choose CSV…"}
          </label>
          <Button
            onClick={runImport}
            disabled={busy || !csvText.trim()}
            data-testid="import-submit-btn"
            className="rounded-full bg-brand text-white hover:bg-brand-deep h-9"
          >
            <Upload size={12} className="mr-2" /> {busy ? "Importing…" : "Import"}
          </Button>
        </div>
      </div>
      {report && (
        <div className="mt-4 rounded-xl bg-gray-50 p-4" data-testid="import-report">
          <p className="text-sm font-medium">
            <span className="text-gain">{report.imported} imported</span>
            {" · "}{report.duplicate} duplicate{report.duplicate === 1 ? "" : "s"}
            {" · "}{report.skipped} skipped
            {" · "}{report.malformed} malformed
            {" · "}{report.total_rows} rows total
            <span className="text-gray-400 font-normal"> · adapter: {report.adapter}</span>
          </p>
          {problems.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1" data-testid="import-report-problems">
              {problems.map((r) => (
                <p key={r.row} className="text-[11px] text-gray-600">
                  <span className={`uppercase font-bold mr-1 ${r.status === "malformed" ? "text-loss" : "text-amber-600"}`}>
                    row {r.row} {r.status}
                  </span>
                  {r.reason}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function Journal() {
  const [trades, setTrades] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // trade id
  const [draft, setDraft] = useState({ tags: "", journal: "", notes: "" });

  const load = async () => {
    const [{ data: t }, { data: tg }] = await Promise.all([
      api.get("/journal", { params: activeTag ? { tag: activeTag } : {} }),
      api.get("/journal/tags"),
    ]);
    setTrades(t);
    setTags(tg);
  };

  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [activeTag]);

  const filtered = useMemo(() => {
    if (!q) return trades;
    const qq = q.toLowerCase();
    return trades.filter(
      (t) =>
        t.symbol.toLowerCase().includes(qq) ||
        (t.notes || "").toLowerCase().includes(qq) ||
        (t.journal || "").toLowerCase().includes(qq) ||
        (t.tags || []).some((tag) => tag.includes(qq))
    );
  }, [trades, q]);

  const startEdit = (t) => {
    setEditing(t._id);
    setDraft({
      tags: (t.tags || []).join(", "),
      journal: t.journal || "",
      notes: t.notes || "",
    });
  };

  const save = async () => {
    if (!editing) return;
    const tagsArr = draft.tags
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    try {
      await api.patch(`/trades/${editing}`, {
        tags: tagsArr,
        journal: draft.journal,
        notes: draft.notes,
      });
      toast.success("Journal entry saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  return (
    <div className="space-y-8" data-testid="journal-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Trade journal</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">Every trade tells a story.</h1>
        <p className="text-gray-500 mt-2 max-w-2xl">
          Log your reasoning, lessons learned, and tag trades by setup. Filter by tag to see how each idea performs over time.
        </p>
      </div>

      <ImportCard onImported={() => load().catch(() => {})} />
      <InsightsCard />

      <div className="flex items-center flex-wrap gap-3">
        <Input
          data-testid="journal-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search symbol, notes, tag…"
          className="max-w-xs h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setActiveTag("")}
            data-testid="tag-all"
            className={`text-xs px-3 py-1.5 rounded-full font-medium ${
              !activeTag ? "bg-brand text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            All
          </button>
          {tags.map((t) => (
            <button
              key={t.tag}
              data-testid={`tag-${t.tag}`}
              onClick={() => setActiveTag(t.tag)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1 ${
                activeTag === t.tag ? "bg-brand text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              <TagIcon size={10} /> {t.tag} <span className="opacity-60">({t.count})</span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="border border-gray-200 rounded-2xl p-10 text-center text-gray-500" data-testid="journal-empty">
          <BookText className="mx-auto mb-3 text-gray-300" size={32} />
          <p>No entries. Trades appear here automatically — click one to add tags and journal notes.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((t) => (
            <Card key={t._id} className="border border-gray-200 rounded-2xl p-5" data-testid={`journal-card-${t._id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display text-lg font-medium">
                    <span className={t.side === "buy" ? "text-gain" : "text-loss"}>{t.side.toUpperCase()}</span>{" "}
                    {fmtNum(t.quantity)} {t.symbol}
                    {t.imported && (
                      <span
                        className="ml-2 text-[9px] uppercase font-bold tracking-wider rounded-full px-1.5 py-0.5 bg-blue-50 text-blue-700 align-middle"
                        data-testid={`imported-badge-${t._id}`}
                      >
                        imported{t.contract_symbol && t.contract_symbol !== t.symbol ? ` · ${t.contract_symbol}` : ""}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    @ {fmtUSD(t.price, { dp: t.price > 10 ? 2 : 4 })} · {relTime(t.created_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono">{fmtUSD(t.total)}</p>
                  {typeof t.realized_pnl === "number" && t.realized_pnl !== 0 && (
                    <p className={`text-xs font-mono ${gainClass(t.realized_pnl)}`} data-testid={`realized-pnl-${t._id}`}>
                      {t.realized_pnl > 0 ? "+" : ""}{fmtUSD(t.realized_pnl)}
                    </p>
                  )}
                  {t.grade && (
                    <Badge variant="secondary" className="text-[10px] mt-1">
                      Grade {t.grade}
                    </Badge>
                  )}
                </div>
              </div>

              {editing === t._id ? (
                <div className="mt-4 space-y-3" data-testid={`journal-edit-${t._id}`}>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500">
                      Tags (comma-separated)
                    </Label>
                    <Input
                      data-testid={`journal-tags-input-${t._id}`}
                      value={draft.tags}
                      onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                      placeholder="breakout, oversold, earnings"
                      className="h-10 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500">Notes</Label>
                    <Input
                      data-testid={`journal-notes-input-${t._id}`}
                      value={draft.notes}
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                      placeholder="One-line reason"
                      className="h-10 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500">
                      Long-form journal
                    </Label>
                    <Textarea
                      data-testid={`journal-longform-${t._id}`}
                      value={draft.journal}
                      onChange={(e) => setDraft({ ...draft, journal: e.target.value })}
                      placeholder="What did you see? What did you learn?"
                      className="rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-1 min-h-[100px] text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={save}
                      data-testid={`journal-save-${t._id}`}
                      className="rounded-full bg-brand text-white hover:bg-brand-deep h-9"
                    >
                      <Save size={12} className="mr-2" /> Save
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditing(null)}
                      className="rounded-full border-gray-200 h-9"
                    >
                      <X size={12} className="mr-2" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  {t.tags?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {t.tags.map((tag) => (
                        <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {t.notes && <p className="text-sm text-gray-700">{t.notes}</p>}
                  {t.journal && <p className="text-sm text-gray-600 whitespace-pre-wrap">{t.journal}</p>}
                  <button
                    onClick={() => startEdit(t)}
                    data-testid={`journal-edit-btn-${t._id}`}
                    className="text-xs text-gray-500 hover:text-brand-deep mt-1"
                  >
                    {t.tags?.length || t.journal ? "Edit" : "Add tags & notes"} →
                  </button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
